"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setVariable = exports.reverseList = exports.getFieldTitle = exports.getRadioOptions = exports.getSchemaPropertyPath = exports.getValueFromObj = exports.hasValueOrForceDisplay = exports.hasAtleastOneValue = exports.makeArray = exports.stringIncludes = exports.isAcceptedOtherFileType = exports.testCondition = exports.zeroIfNaN = exports.isNumber = exports.uniqueLength = exports.length = exports.parseJson = void 0;
const date_fns_1 = require("date-fns");
const json_ptr_1 = require("json-ptr");
/**
 * Converts a dot-notation path to a json pointer path. Not an exported helper.
 * @ignore
 * @param { string } dotPath dot-notation path to be converted to json pointer path
 * @returns { string } converted path in json pointer format
 * @example convertDotPathToJsonPointer("path/to/value") => "path.to.value"
 */
const convertDotPathToJsonPointer = (dotPath) => {
    // in case we accidentally get a json path that starts with a slash, remove it, it will be added at the end
    if (dotPath.startsWith("/")) {
        dotPath = dotPath.slice(1);
    }
    const regex = /\./g;
    return `/${dotPath.replace(regex, "/")}`;
};
/**
 * Parses the JSON string into an object
 *
 * This is a workaround for the fact that Handlebars won't let you declare
 * arrays or objects as arguments to helpers in templates
 * @param { string } str Value to be parsed
 * @returns { string | boolean | Array | number | Record<string, any> } results of JSON.parse
 * @example parseJson('["red", "blue", "green"]') => ["red", "blue", "green"] as array
 * @example parseJson('true') => true as boolean
 */
const parseJson = (str) => {
    return JSON.parse(str);
};
exports.parseJson = parseJson;
/**
 * Return the length of the given countable object
 * @param { Array<any> | string }countable List to check length or string to check length of
 * @returns { number } Length of countage
 * @example length([1, 2, 3, 4, 5]) => 5
 */
const length = (countable) => {
    var _a;
    return (_a = countable === null || countable === void 0 ? void 0 : countable.length) !== null && _a !== void 0 ? _a : 0;
};
exports.length = length;
/**
 * Return the unique length of the given countable object by providing a specific path to a key. Neither duplicate entries, null values, or empty strings will be counted.
 * @param { Array<any> | string }countable List to check length or string to check length of
 * @param { string | Array<string | Record<string, any>> } pathToValue - dot-notation path to the value - can be nested (ie employee.company_supplied_id).
 * @returns { number } Length of uniquely counted elements
 * @example uniqueLength([{"top_level": {"nested_level": "Nested Value1"}}, {"top_level": {"nested_level": ""}}, {"top_level": {"nested_level": "Nested Value1"}}, {"top_level": {"nested_level": null}}], "top_level.nested_level") => 1
 * @example uniqueLength([{"employee": {"company_supplied_id": "490"}}, {"employee": {"company_supplied_id": "491"}}, {"employee": {"company_supplied_id": "490"}}], "employee.company_supplied_id") => 2
 */
const uniqueLength = (obj, pathToValue) => {
    if (!obj || typeof obj !== "object") {
        return 0;
    }
    else {
        const value = Object.values(obj);
        let uniqueValueSet = [];
        value.forEach(value => {
            const pathValue = (0, exports.getValueFromObj)(value, pathToValue);
            if (pathValue) {
                uniqueValueSet.push(pathValue);
            }
        });
        let uniqueArrayLength = [...new Set(uniqueValueSet)].length;
        return uniqueArrayLength;
    }
};
exports.uniqueLength = uniqueLength;
/**
 * Takes in a value and returns a boolean based on if the value is a number
 * @param { any } value Value to be checked
 * @returns { boolean } If value is a number, returns true, otherwise false
 * @example isNumber(10) => true
 * @example isNumber("not a number") => false
 */
const isNumber = (value) => typeof value === "number" && !isNaN(value);
exports.isNumber = isNumber;
/**
 * Given a value, returns the value if it's a number, or a 0 if it is NaN
 * @param { number } value - the value to check as a number
 * @returns { number } number value or 0
 * @example isNumber(10) => 10
 * @example isNumber("not a number") => 0
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const zeroIfNaN = (value) => {
    return !(0, exports.isNumber)(value) ? 0 : value;
};
exports.zeroIfNaN = zeroIfNaN;
/**
 * Apply fixed set of boolean tests to two inputs. Does not work on objects.
 * @param { number|string|boolean|undefined|Date } condition1 First value to compare
 * @param { string } comparator Comparison operator to use for the comparison.
 *                              Acceptable operators are "===", "!==", "<", "<=", ">", ">=".
 * @param {number|string|boolean|undefined|Date } condition2 Second value to compare
 * @returns { boolean } Result of the comparison
 * @example testCondition(15, ">=", 20) => false
 * @example testCondition("blue", "===", "blue") => true
 * @example testCondition(15, "!==", 20) => true
 */
const testCondition = (condition1, comparator, condition2) => {
    if (!["===", "!==", "<", "<=", ">", ">="].includes(comparator)) {
        return false;
    }
    // We want to make sure both conditions are accepted types/dates
    const acceptedTypes = ["number", "string", "boolean"];
    const isAcceptedType = acceptedTypes.includes(typeof condition1) && acceptedTypes.includes(typeof condition2);
    if (!isAcceptedType && !((0, date_fns_1.isDate)(condition1) && (0, date_fns_1.isDate)(condition2))) {
        return false;
    }
    condition1 = JSON.stringify(condition1);
    condition2 = JSON.stringify(condition2);
    return eval(`(${condition1}) ${comparator} (${condition2})`);
};
exports.testCondition = testCondition;
/**
 * Determine if the file type is one of the accepted "other" types
 * @param { string } type The value to find
 * @returns { boolean } The result of the search
 * @example isAcceptedOtherFileType("text/csv") => true
 * @example isAcceptedOtherFileType("image/png") => false
 */
const isAcceptedOtherFileType = (type) => {
    return [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ].includes(type);
};
exports.isAcceptedOtherFileType = isAcceptedOtherFileType;
/**
 * Determine if a string contains a substring
 * @param { string } string The string to search
 * @param { string } substring The string to find
 * @returns { boolean } The result of the search
 * @example stringIncludes("How now brown cow", "cow") => true
 * @example stringIncludes("How now brown cow", "sow") => false
 */
const stringIncludes = (string, substring) => {
    if (!string || !substring) {
        return false;
    }
    return string.includes(substring);
};
exports.stringIncludes = stringIncludes;
/**
 * A helper used to generate arrays since Handlebars cannot natively take in arrays
 * @param data All args passed to helper will be converted into an array
 * @returns { Array<number | string> } All args in an array
 * @example {{makeArray "dog" "cat" "pig"}} => ["dog", "cat", "pig"]
 */
const makeArray = (...data) => data;
exports.makeArray = makeArray;
/**
 * Takes in an object, checks the values of the object and returns true if any values are not null/false
 * @param { Object } obj Object to check for a truthy value
 * @returns { boolean } If there is a truthy value in the object, returns true, else false
 * @example hasAtleastOneValue({"top_level": "Great Value!"}) => true
 * @example hasAtleastOneValue({"top_level": false}) => false
 * @example hasAtleastOneValue({"top_level": {"nested_level": 5}}) => true
 * @example hasAtleastOneValue({"top_level": {"nested_level": null}}) => false
 */
const hasAtleastOneValue = (obj) => {
    if (!obj || typeof obj !== "object") {
        return false;
    }
    let hasValue = false;
    const values = Object.values(obj);
    for (let i = 0; i < values.length; i++) {
        if (values[i]) {
            if (typeof values[i] === "object") {
                return (0, exports.hasAtleastOneValue)(values[i]);
            }
            else {
                hasValue = true;
                break;
            }
        }
    }
    return hasValue;
};
exports.hasAtleastOneValue = hasAtleastOneValue;
/**
 * Checks if an object has a value, or if we want to force the display. Used in signature blocks.
 * @param { Record<string, any> } obj Object to check for a truthy value
 * @param { boolean } forceDisplay Optional. Default is false. If true, will force result to be true
 * @returns { boolean } Result will be the forceDisplay value if given or fallback to checking the object for any truthy values.
 * @example hasValueOrForceDisplay(null, true) => true
 * @example hasValueOrForceDisplay({"top_level": "Great Value!"}) => true
 * @example hasValueOrForceDisplay({"top_level": null}) => false
 */
const hasValueOrForceDisplay = (obj, forceDisplay = false) => {
    return typeof forceDisplay === "boolean" && forceDisplay ? true : (0, exports.hasAtleastOneValue)(obj);
};
exports.hasValueOrForceDisplay = hasValueOrForceDisplay;
/**
 * Helper to pull a value from an object. Path is expected in a dot-notation format.
 * @param { Record<string, any> } obj - the Object we are extracting the information from
 * @param { string | Array<string | Record<string, any>> } pathToValue - dot-notation path to the value - can be nested (ie employee.first_name). Can also be a list of paths.
 * @param { string } separator - Optional, if pathToValue is a list of keys, this will be used as the separator for the values. Default is ", "
 * @returns { string } Value extracted from the object or an empty string if no value is available
 * @example getValueFromObj({"top_level": {"nested_level": "Nested Value"}}, "top_level.nested_level") => "Nested Value"
 * @example getValueFromObj("employee": {"first_name": "Sally", "last_name": "Williams"}, ["employee.last_name", "employee_first_name"]) => "Williams, Sally"
 */
const getValueFromObj = (obj, pathToValue, separator) => {
    let value = "";
    if (typeof separator !== "string") {
        separator = ", ";
    }
    // we allow you to pass a list of keys to be combined into a single cell
    if (Array.isArray(pathToValue)) {
        const values = [];
        pathToValue.forEach((path) => {
            if (typeof path === "string") {
                const jsonPath = convertDotPathToJsonPointer(path);
                const pathValue = `${json_ptr_1.JsonPointer.get(obj, jsonPath)}`;
                if (pathValue)
                    values.push(pathValue);
            }
        });
        value = values.join(separator);
    }
    else if (typeof pathToValue === "string") {
        const jsonPath = convertDotPathToJsonPointer(pathToValue);
        const pathValue = `${json_ptr_1.JsonPointer.get(obj, jsonPath)}`;
        if (pathValue)
            value = pathValue;
    }
    return ["null", "undefined"].includes(value) ? "" : `${value}`;
};
exports.getValueFromObj = getValueFromObj;
/**
 * Helper to get the path to a field in a schema by adding "properties" to the path.
 * Can take a dot notation or json path
 * @param { string } path - Path to the value in the store, we will convert it to a schema path
 * @returns { string } Path through the schema properties
 * @example getSchemaPropertyPath("/daily_report/location") => "/properties/daily_report/properties/location"
 * @example getSchemaPropertyPath("daily_report.location") => "/properties/daily_report/properties/location"
 */
const getSchemaPropertyPath = (path, schema) => {
    let jsonPath = convertDotPathToJsonPointer(`${path}`);
    const fields = jsonPath.split("/");
    let finalPath = "/properties";
    let currentSection = schema === null || schema === void 0 ? void 0 : schema.properties;
    fields.forEach((pathName, index) => {
        var _a;
        if (!pathName)
            return;
        if (currentSection === null || currentSection === void 0 ? void 0 : currentSection[pathName]) {
            finalPath += `/${pathName}`;
            currentSection = (_a = currentSection[pathName]) === null || _a === void 0 ? void 0 : _a.properties;
            if (currentSection && index + 1 !== fields.length)
                finalPath += "/properties";
        }
    });
    return finalPath;
};
exports.getSchemaPropertyPath = getSchemaPropertyPath;
/**
 * Get the enum/radio options from the provided schema
 * @param { Record<string, any> } schema - the Object we are extracting the information from
 * @param { string } pathToValue - dot-notation path to the value - can be nested (ie employee.first_name).
 * @returns { string } List of radio options
 * @example getRadioOptions({"properties": {"radio_field": {"enum": ["Yes", "No", "N/A"]}}} "radio_field") => ["Yes", "No", "N/A"]
 * @example getRadioOptions({"properties": {"top_level": {"properties": {"radio_field": {"enum": ["Yes", "No", "N/A"]}}}}} "top_level.radio_field") => ["Yes", "No", "N/A"]
 */
const getRadioOptions = (schema, pathToValue) => {
    return json_ptr_1.JsonPointer.get(schema, `${(0, exports.getSchemaPropertyPath)(pathToValue, schema)}/enum`);
};
exports.getRadioOptions = getRadioOptions;
/**
 * Get the field title from the provided schema
 * @param { Record<string, any> } schema - the Object we are extracting the information from
 * @param { string } pathToValue - dot-notation path to the value - can be nested (ie employee.first_name).
 * @returns { string } Display title for the field
 * @example getFieldTitle({"properties": {"radio_field": {"title": "Awesome Title""}}} "radio_field") => "Awesome Title"
 * @example getFieldTitle({"properties": {"top_level": {"properties": {"radio_field": {"title": "Awesome Title"}}}}} "top_level.radio_field") => "Awesome Title"
 */
const getFieldTitle = (schema, path) => {
    return json_ptr_1.JsonPointer.get(schema, `${(0, exports.getSchemaPropertyPath)(path, schema)}/title`);
};
exports.getFieldTitle = getFieldTitle;
/**
 * Helper to call JS method 'reverse' on the provided array
 * @param listToReverse
 * @returns array in reverse order
 */
const reverseList = (listToReverse) => {
    if (!Array.isArray(listToReverse))
        return listToReverse;
    return listToReverse.reverse();
};
exports.reverseList = reverseList;
/**
 * Can be used to create a local variable within the transform template to reuse by tagging the @root objects
 * {{ set_variable "total_hours" (get_tm_ticket_labor_total store.subcontractor_use) }}
 * {{ @root.total_hours }} -> would be the result of get_tm_ticket_labor_total store.subcontractor_use

 * @param varName: name of the variable, this is how you will refeference it from @root. Make the name clear.
 * @param varValue: value to save for that variable name
 * @param options  Handlebars built-in options so we can set the variable at the root
 */
const setVariable = (varName, varValue, options) => {
    options.data.root[varName] = varValue;
};
exports.setVariable = setVariable;
